import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { resolveProductRetailerForWishlist } from '../utils/watchlistRetailer';
import { trackMetaAddToWishlist } from '../utils/analytics';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children, currentUser }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!currentUser) {
      const guestWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
      const now = Date.now();
      // Increased persistence to 30 days (2592000000 ms)
      let validItems = guestWishlist.filter(item => (now - item.addedAt) < 2592000000);

      // Heal older guest items: partnered products + correct retailer (store) name
      const idsNeedingLookup = validItems
        .map((item) => item.productId)
        .filter(Boolean);

      if (idsNeedingLookup.length > 0) {
        try {
          const res = await api.get('/api/products/partnered-status', {
            params: { ids: [...new Set(idsNeedingLookup)].join(',') },
          });
          const flags = res.data?.flags || {};
          const retailers = res.data?.retailers || {};
          validItems = validItems.map((item) => {
            const id = String(item.productId);
            const partnered = !!(item.isPartnered || item.size === 'ONESIZE' || flags[id]);
            // Always prefer live catalogue retailer (store) over stale brand-as-retailer
            const retailerName = retailers[id] || item.retailer || item.merchantName;
            const productBrand = item.productBrand || item.brand;
            return {
              ...item,
              isPartnered: partnered || item.isPartnered || false,
              size: partnered ? 'ONESIZE' : item.size,
              isAvailable: partnered ? true : item.isAvailable,
              retailer: retailerName,
              merchantName: retailerName || item.merchantName,
              productBrand,
            };
          });
        } catch (e) {
          console.error('Guest watchlist partnered heal error:', e);
        }
      } else {
        validItems = validItems.map((item) => (
          item.isPartnered || item.size === 'ONESIZE'
            ? { ...item, isPartnered: true, size: 'ONESIZE', isAvailable: true }
            : item
        ));
      }

      localStorage.setItem('guest_wishlist', JSON.stringify(validItems));
      setWishlistItems(validItems);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/api/wishlist', { params: { checkAvailability: 'true' } });

      if (response.data && response.data.success) {
        setWishlistItems(response.data.wishlist);
      }
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const toggleWishlist = async (productData, selectedSize = 'M') => {
    if (!productData) return { success: false, action: 'error' };

    // Robust ID extraction
    const rawId = productData._id || productData.productId || productData.id;
    if (!rawId) {
      console.error('WATCHLIST_ERROR: No valid ID found for product', productData);
      return { success: false, action: 'error' };
    }
    
    const productId = String(rawId);
    const isPartnered = !!productData.isPartnered;
    const size = isPartnered ? 'ONESIZE' : (selectedSize || 'M');
    // Simple mock detection: if it's explicitly a guest ID
    const isGuestProduct = productId.startsWith('guest_');

    console.log('WATCHLIST_TOGGLE_DEBUG:', { productId, isGuest: !currentUser, isGuestProduct, isPartnered, size });

    if (!currentUser || isGuestProduct) {
      try {
        const guestWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
        const existingIndex = guestWishlist.findIndex(item => 
          String(item.productId) === productId || String(item._id) === productId
        );
        
        let newWishlist;
        let action;

        if (existingIndex > -1) {
          newWishlist = guestWishlist.filter((_, idx) => idx !== existingIndex);
          action = 'removed';
        } else {
          const retailerName = resolveProductRetailerForWishlist(productData);
          const newItem = {
            _id: `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            productId,
            productName: productData.name,
            productImage: productData.image || (productData.images && productData.images[0]) || '/images/default-product.jpg',
            affiliateUrl: productData.affiliateUrl,
            productPrice: parseFloat(String(productData.price || 0).replace(/[^0-9.]/g, '')),
            productBrand: productData.brand || 'Unknown',
            merchantName: retailerName,
            retailer: retailerName,
            originalPrice: productData.originalPrice ? parseFloat(String(productData.originalPrice).replace(/[^0-9.]/g, '')) : undefined,
            discount: productData.discount || 0,
            size,
            isPartnered,
            isAvailable: true,
            addedAt: Date.now()
          };
          newWishlist = [newItem, ...guestWishlist];
          action = 'added';
        }

        localStorage.setItem('guest_wishlist', JSON.stringify(newWishlist));
        setWishlistItems([...newWishlist]);
        if (action === 'added') trackMetaAddToWishlist(productData);
        return { success: true, action };
      } catch (e) {
        console.error('Guest watchlist error:', e);
        return { success: false, action: 'error' };
      }
    }

    try {
      const retailerName = resolveProductRetailerForWishlist(productData);
      const response = await api.post('/api/wishlist/toggle', {
        productId,
        size,
        productData: {
          name: productData.name,
          image: productData.image || (productData.images && productData.images[0]) || '/images/default-product.jpg',
          affiliateUrl: productData.affiliateUrl,
          price: parseFloat(String(productData.price || 0).replace(/[^0-9.]/g, '')),
          brand: productData.brand || 'Unknown',
          merchantName: retailerName,
          retailer: retailerName,
          originalPrice: productData.originalPrice ? parseFloat(String(productData.originalPrice).replace(/[^0-9.]/g, '')) : undefined,
          discount: productData.discount || 0,
          isPartnered
        }
      });

      if (response.data && response.data.success) {
        const result = response.data;
        
        if (result.action === 'removed') {
          setWishlistItems(prev => prev.filter(item => 
            String(item.productId) !== productId && String(item._id) !== productId
          ));
        } else if (result.item) {
          // Add the item returned from server immediately
          setWishlistItems(prev => {
            const exists = prev.some(item => String(item._id) === String(result.item._id));
            if (exists) return prev;
            return [result.item, ...prev];
          });
        } else {
          // Fallback to full fetch if item not returned
          await fetchWishlist();
        }
        
        if (result.action === 'added') trackMetaAddToWishlist(productData);
        return { success: true, action: result.action };
      } else {
        console.error('Server watchlist toggle failed:', response.data);
        return { success: false, action: 'error', error: response.data?.error };
      }
    } catch (err) {
      console.error('Server watchlist error:', err);
      return { success: false, action: 'error' };
    }
  };

  const removeFromWishlist = async (wishlistId) => {
    if (!wishlistId) return false;
    const idStr = String(wishlistId);
    const isGuestId = idStr.startsWith('guest_');

    if (!currentUser || isGuestId) {
      const guestWishlist = JSON.parse(localStorage.getItem('guest_wishlist') || '[]');
      const hasItemId = guestWishlist.some(item => String(item._id) === idStr);
      const newWishlist = guestWishlist.filter(item =>
        hasItemId
          ? String(item._id) !== idStr
          : String(item.productId) !== idStr
      );
      localStorage.setItem('guest_wishlist', JSON.stringify(newWishlist));
      setWishlistItems([...newWishlist]);
      return true;
    }

    // Remove optimistically so stale items disappear even when the API is slow.
    const previousWishlist = wishlistItems;
    const hasWishlistId = previousWishlist.some(item => String(item._id) === idStr);
    setWishlistItems(prev => prev.filter(item =>
      hasWishlistId
        ? String(item._id) !== idStr
        : String(item.productId) !== idStr
    ));

    try {
      const response = await api.delete(`/api/wishlist/item/${encodeURIComponent(idStr)}`);

      if (response.data && response.data.success) {
        return true;
      }
      setWishlistItems(previousWishlist);
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      // A 404 means it was already removed server-side; keep the optimistic UI.
      if (err.response?.status !== 404) setWishlistItems(previousWishlist);
      return err.response?.status === 404;
    }
    return false;
  };

  const isInWishlist = useCallback((productId) => {
    if (!productId) return false;
    const idStr = String(productId).toLowerCase();
    return wishlistItems.some(item => {
      const itemProductId = item.productId ? String(item.productId).toLowerCase() : null;
      const itemId = item._id ? String(item._id).toLowerCase() : null;
      return itemProductId === idStr || itemId === idStr;
    });
  }, [wishlistItems]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Debug log for wishlist changes
  useEffect(() => {
    console.log('WATCHLIST_STATE_UPDATE:', wishlistItems.length, 'items');
  }, [wishlistItems]);

  return (
    <WishlistContext.Provider value={{
      wishlistItems,
      wishlistCount: wishlistItems.length,
      loading,
      toggleWishlist,
      removeFromWishlist,
      isInWishlist,
      refreshWishlist: fetchWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
};
